import Config from '@/utils/config'

// schema.org data for search engines. Facts that never change between
// languages live here; translated text is passed in by the pages.

const baseUrl = () => Config.baseUrl.replace(/\/$/, '')

export const organizationId = () => `${baseUrl()}/#organization`

/** The company as a local business, matching the Google Business Profile. */
export const organization = (image: string): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': organizationId(),
  name: 'ZNIPP GmbH',
  legalName: 'ZNIPP GmbH',
  url: baseUrl(),
  image: image.startsWith('/') ? baseUrl() + image : image,
  email: 'contact@znipp.ch',
  telephone: '+41 41 589 80 75',
  // The UID as the register carries it; the VAT number is the same UID with
  // the MWST suffix (see the imprint).
  vatID: 'CHE-105.642.181 MWST',
  taxID: 'CHE-105.642.181',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Underschwändlistrasse 10',
    postalCode: '6048',
    addressLocality: 'Horw',
    addressRegion: 'LU',
    addressCountry: 'CH',
  },
  areaServed: { '@type': 'Country', name: 'Switzerland' },
  sameAs: [Config.linkedinZnipp, Config.facebookZnipp],
})

/** A service offered by the company, with an offer only when it has a fixed price. */
export const service = (options: {
  name: string
  description: string
  url: string
  price?: string
}): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: options.name,
  description: options.description,
  url: options.url,
  provider: { '@id': organizationId() },
  areaServed: { '@type': 'Country', name: 'Switzerland' },
  ...(options.price && {
    offers: {
      '@type': 'Offer',
      price: options.price,
      priceCurrency: 'CHF',
      url: options.url,
    },
  }),
})

export const faqPage = (
  items: { question: string; answer: string }[]
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
})

/** A blog article written by the founder and published by the company. */
export const blogPosting = (options: {
  headline: string
  description: string
  url: string
  image: string
  datePublished: string
  inLanguage: string
}): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: options.headline,
  description: options.description,
  url: options.url,
  mainEntityOfPage: options.url,
  image: options.image.startsWith('/') ? baseUrl() + options.image : options.image,
  datePublished: options.datePublished,
  inLanguage: options.inLanguage,
  author: {
    '@type': 'Person',
    name: 'Stefan Hess',
    url: Config.linkedinStefan,
  },
  publisher: { '@id': organizationId() },
})
